/**
 * WebSocket hook for real-time live match updates.
 *
 * Phase 12a: Real-Time Updates
 * - Connects to backend WebSocket for instant score/stats updates
 * - Falls back to polling if WebSocket connection fails
 * - Handles reconnection with exponential backoff
 */

import { useEffect, useRef, useCallback, useState } from 'react'

// WebSocket message types from backend
export interface LiveScoreUpdate {
  type: 'fixture_update'
  fixture_id: string
  data: {
    home_score: number | null
    away_score: number | null
    status: string
    status_detail: string
    elapsed: number | null
    elapsed_extra: number | null
  }
}

export interface LiveStatsUpdate {
  type: 'stats_update'
  fixture_id: string
  data: {
    home_corners?: number
    away_corners?: number
    home_shots?: number
    away_shots?: number
    home_shots_on_target?: number
    away_shots_on_target?: number
    home_possession?: number
    away_possession?: number
    home_fouls?: number
    away_fouls?: number
    home_yellows?: number
    away_yellows?: number
    home_xg?: number
    away_xg?: number
  }
}

export interface LiveEventUpdate {
  type: 'event'
  fixture_id: string
  data: {
    event_type: string
    elapsed: number
    team_id?: string
    player_name?: string
    detail?: string
  }
}

export interface BookingWarningUpdate {
  type: 'booking_warning'
  fixture_id: string
  data: {
    player_id: number
    player_name: string
    team_id: number
    team_name: string
    reason: string
    risk_level: 'medium' | 'high'
    elapsed_minute: number
    message: string
  }
}

export type LiveUpdate = LiveScoreUpdate | LiveStatsUpdate | LiveEventUpdate | BookingWarningUpdate

export interface UseLiveUpdatesOptions {
  /** Callback when any update is received */
  onUpdate?: (update: LiveUpdate) => void
  /** Specific fixture ID to subscribe to (optional) */
  fixtureId?: string
  /** Whether to enable the WebSocket connection (default: true) */
  enabled?: boolean
  /** Heartbeat interval in ms (default: 30000) */
  heartbeatInterval?: number
  /** Max reconnection attempts (default: 5) */
  maxReconnectAttempts?: number
}

export interface UseLiveUpdatesResult {
  /** Whether WebSocket is currently connected */
  isConnected: boolean
  /** Connection error message if any */
  error: string | null
  /** Number of reconnection attempts made */
  reconnectAttempts: number
  /** Manually reconnect */
  reconnect: () => void
  /** Subscribe to a specific fixture (in addition to live feed) */
  subscribeToFixture: (fixtureId: string) => void
  /** Unsubscribe from a specific fixture */
  unsubscribeFromFixture: (fixtureId: string) => void
}

/**
 * Get WebSocket URL from environment or derive from API base
 */
function getWebSocketUrl(fixtureId?: string): string {
  // Check for explicit WebSocket URL
  const wsUrl = import.meta.env.VITE_WS_URL
  if (wsUrl) {
    return fixtureId ? `${wsUrl}/v1/ws/fixture/${fixtureId}` : `${wsUrl}/v1/ws/live`
  }

  // Derive from API base URL
  const apiBase = import.meta.env.VITE_API_BASE || 'http://localhost:8005/v1'
  const wsBase = apiBase
    .replace(/^http:/, 'ws:')
    .replace(/^https:/, 'wss:')
    .replace(/\/v1$/, '')

  return fixtureId ? `${wsBase}/v1/ws/fixture/${fixtureId}` : `${wsBase}/v1/ws/live`
}

/**
 * Hook to subscribe to live fixture updates via WebSocket.
 *
 * @example
 * // Subscribe to all live updates
 * const { isConnected } = useLiveUpdates({
 *   onUpdate: (update) => {
 *     if (update.type === 'fixture_update') {
 *       // Update fixture in state
 *     }
 *   }
 * })
 *
 * @example
 * // Subscribe to a specific fixture
 * const { isConnected } = useLiveUpdates({
 *   fixtureId: 'abc123',
 *   onUpdate: handleUpdate
 * })
 */
export function useLiveUpdates(options: UseLiveUpdatesOptions = {}): UseLiveUpdatesResult {
  const {
    onUpdate,
    fixtureId,
    enabled = true,
    heartbeatInterval = 30000,
    maxReconnectAttempts = 5,
  } = options

  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reconnectAttempts, setReconnectAttempts] = useState(0)

  const wsRef = useRef<WebSocket | null>(null)
  const heartbeatRef = useRef<number | null>(null)
  const reconnectTimeoutRef = useRef<number | null>(null)
  const onUpdateRef = useRef(onUpdate)

  // Keep onUpdate ref current
  useEffect(() => {
    onUpdateRef.current = onUpdate
  }, [onUpdate])

  const clearTimers = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current)
      heartbeatRef.current = null
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
  }, [])

  const connect = useCallback(() => {
    if (!enabled) return

    // Clean up existing connection
    if (wsRef.current) {
      wsRef.current.close()
    }
    clearTimers()

    const url = getWebSocketUrl(fixtureId)

    try {
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        setIsConnected(true)
        setError(null)
        setReconnectAttempts(0)

        // Start heartbeat
        heartbeatRef.current = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }))
          }
        }, heartbeatInterval)
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as LiveUpdate | { type: 'pong' | 'subscribed' | 'unsubscribed' }

          // Ignore internal messages
          if (data.type === 'pong' || data.type === 'subscribed' || data.type === 'unsubscribed') {
            return
          }

          // Call update handler
          if (onUpdateRef.current) {
            onUpdateRef.current(data as LiveUpdate)
          }
        } catch (e) {
          console.warn('Failed to parse WebSocket message:', e)
        }
      }

      ws.onerror = (event) => {
        console.error('WebSocket error:', event)
        setError('Connection error')
      }

      ws.onclose = (event) => {
        setIsConnected(false)
        clearTimers()

        // Don't reconnect if intentionally closed or disabled
        if (!enabled || event.code === 1000) {
          return
        }

        // Attempt reconnection with exponential backoff
        setReconnectAttempts((prev) => {
          const nextAttempt = prev + 1
          if (nextAttempt <= maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, prev), 30000) // Max 30s
            reconnectTimeoutRef.current = window.setTimeout(() => {
              connect()
            }, delay)
            setError(`Reconnecting in ${delay / 1000}s...`)
          } else {
            setError('Connection lost. Click to retry.')
          }
          return nextAttempt
        })
      }
    } catch (e) {
      setError('Failed to connect')
      console.error('WebSocket connection failed:', e)
    }
  }, [enabled, fixtureId, heartbeatInterval, maxReconnectAttempts, clearTimers])

  const reconnect = useCallback(() => {
    setReconnectAttempts(0)
    setError(null)
    connect()
  }, [connect])

  const subscribeToFixture = useCallback((fid: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'subscribe_fixture',
        fixture_id: fid,
      }))
    }
  }, [])

  const unsubscribeFromFixture = useCallback((fid: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'unsubscribe_fixture',
        fixture_id: fid,
      }))
    }
  }, [])

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    if (enabled) {
      connect()
    }

    return () => {
      clearTimers()
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted')
        wsRef.current = null
      }
    }
  }, [enabled, connect, clearTimers])

  return {
    isConnected,
    error,
    reconnectAttempts,
    reconnect,
    subscribeToFixture,
    unsubscribeFromFixture,
  }
}
